import type {
    FinancingTerm,
    PriceItem,
    PriceTab
} from '@/types'

export const priceTabs: PriceTab[] = ['Springs', 'Cables & rollers', 'Openers', 'New doors']

export const priceItems: PriceItem[] = [
    { id: 'torsion-pair', tab: 'Springs', name: "Torsion springs, matched pair", note: "Standard springs, rated for 10,000 cycles", lo: 225, hi: 345, group: 'torsion', formService: 'spring', answers: { spring_type: "Torsion (above the door)" } },
    { id: 'torsion-hc', tab: 'Springs', name: "High-cycle torsion springs", note: "Built for 20,000+ cycles, covered for life", lo: 295, hi: 395, group: 'torsion', formService: 'spring', answers: { spring_type: "Torsion (above the door)" } },
    { id: 'extension', tab: 'Springs', name: "Extension springs, pair", note: "Fitted with safety cables", lo: 165, hi: 245, formService: 'spring', answers: { spring_type: "Extension (along the tracks)" } },
    { id: 'cables', tab: 'Cables & rollers', name: "Lift cables, pair", note: "Door rebalanced as part of the job", lo: 150, hi: 275, formService: 'offtrack' },
    { id: 'rollers', tab: 'Cables & rollers', name: "Nylon rollers, full set", note: "Sealed bearings for a quieter run", lo: 120, hi: 220, formService: 'tune-up' },
    { id: 'track', tab: 'Cables & rollers', name: "Off-track repair", note: "Rollers reseated, track straightened", lo: 175, hi: 350, formService: 'offtrack' },
    { id: 'tuneup', tab: 'Cables & rollers', name: "Tune-up & safety check", note: "Includes the 21-point inspection", lo: 95, hi: 165, formService: 'tune-up' },
    { id: 'op-repair', tab: 'Openers', name: "Opener repair", note: "Gears, logic boards, sensors or remotes", lo: 125, hi: 295, formService: 'opener', answers: { opener_need: "Repair my opener" } },
    { id: 'op-chain', tab: 'Openers', name: "Chain-drive opener", note: "Supplied and fitted", lo: 450, hi: 600, group: 'opener', formService: 'opener', answers: { opener_need: "Replace my opener" } },
    { id: 'op-belt', tab: 'Openers', name: "Belt-drive smart opener", note: "Wi-Fi and battery backup, supplied and fitted", lo: 620, hi: 900, group: 'opener', formService: 'opener', answers: { opener_need: "Replace my opener" } },
    { id: 'op-wall', tab: 'Openers', name: "Wall-mount opener", note: "The quietest option, ceiling left clear", lo: 890, hi: 1250, group: 'opener', formService: 'opener', answers: { opener_need: "Replace my opener" } },
    { id: 'panel', tab: 'New doors', name: "Replace one damaged panel", note: "Matched to your door's maker and color", lo: 250, hi: 800, formService: 'panel' },
    { id: 'door-single', tab: 'New doors', name: "New single door (9×7)", note: "Fitted, with the old door taken away", lo: 1150, hi: 2400, group: 'newdoor', formService: 'new-door', answers: { door_size: "Single (about 9 ft wide)" } },
    { id: 'door-double', tab: 'New doors', name: "New double door (16×7)", note: "Fitted, with the old door taken away", lo: 1800, hi: 4800, group: 'newdoor', formService: 'new-door', answers: { door_size: "Double (about 16 ft wide)" } }
]

export const defaultPriceSelection: string[] = ['torsion-pair']

export const PRICE_FINEPRINT = "Every range includes parts, labor and haul-away. When we do the repair there is no trip fee, and your technician confirms the exact figure before any work starts."

export const fees: { label: string, value: string, note: string }[] = [
    { label: "Diagnostic visit", value: "$59", note: "Dropped entirely when we carry out the repair" },
    { label: "Call-outs after 7 PM", value: "+$75", note: "One flat charge, mentioned on the phone before we set off" },
    { label: "Haul-away", value: "Included", note: "We take old parts and old doors with us" },
    { label: "Saturdays", value: "No surcharge", note: "Same prices from 8 AM to 6 PM" }
]

export const financing = {
    min: 1000,
    max: 10000,
    step: 100,
    start: 3200,
    defaultMonths: 60,
    terms: [
        { months: 12, apr: 0 },
        { months: 24, apr: 7.99 },
        { months: 36, apr: 9.99 },
        { months: 60, apr: 9.99 }
    ] as FinancingTerm[],
    fineprint: "An estimate only. Financing comes from a third-party lender and depends on credit approval. Rates run from 0% to 17.99% APR.",
    steps: [
        "See your rate in about two minutes, with no effect on your credit score.",
        "Choose a term that suits you. 12 months comes at 0% APR on approved credit.",
        "We fit the door, and you pay the lender month by month."
    ]
}

export const monthlyPayment = (amount: number, term: FinancingTerm): number => {
    if (term.apr === 0) return amount / term.months

    const rate = term.apr / 100 / 12

    return (amount * rate) / (1 - Math.pow(1 + rate, -term.months))
}
